import Cookies from 'js-cookie';
import { AuthContextProps } from 'react-oidc-context';
import { randomNumber } from '@/util/number';
import { DynamicConfig } from '@/config/config';
import { isViatelProfile } from '@/types/user';
import { CookieKeys } from '@/configuration/cookies';
import { isAfter } from './dateUtil';

/**
 * Create an ID for temporary local storage object used for authentication.
 * @param auth AuthContextProps
 */
const generateOidcId = (auth: AuthContextProps): string =>
  auth.user?.session_state || randomNumber(42973, 100000000) + '';

/**
 * Prefix that oidc-client-ts' WebStorageStateStore uses for every entry it keeps
 * in local storage.
 */
const OIDC_STORAGE_PREFIX = 'oidc.';

/**
 * Current time in the same unit oidc-client-ts uses for {@link State.created},
 * i.e. epoch *seconds*. Using milliseconds here would place the entry far in the
 * future and make oidc-client-ts' own garbage collection skip it forever.
 */
const epochSeconds = (): number => Math.floor(new Date().getTime() / 1000);

/**
 * Generate temporary auth object
 * @param config DynamicConfig
 * @param oidcId ID to use. Important it's the same as used in key for local storage.
 */
const generateOidcObj = (config: DynamicConfig, oidcId: string) => {
  return {
    id: oidcId,
    created: epochSeconds(),
    request_type: 'si:r',
    authority: config.auth.url,
    client_id: config.auth.client_id,
    redirect_uri: window.location.origin + '/openid/callback',
    scope: config.auth.scope,
    client_secret: config.auth.client_secret,
    response_mode: 'query'
  };
};

/**
 * Generate a URL that should be similar to regular auth URL.
 * Only difference should be that the path "/connect/authorize"
 * is now "/user/select"
 * @param config The currently used {@link DynamicConfig}
 * @param auth The currently used auth object from {@link AuthContextProps}
 * @param oidcId ID to use. Important it's the same as used in key for local storage.
 * @returns
 */
const generateUrlForUserSelect = (config: DynamicConfig, auth: AuthContextProps, oidcId: string): URL => {
  const url = new URL(`${config.auth.url}/user/select`);
  const params = url.searchParams;
  params.append('client_id', auth.settings.client_id);
  params.append('redirect_uri', auth.settings.redirect_uri);
  params.append('response_mode', auth.settings.response_mode || '');
  params.append('response_type', auth.settings.response_type || '');
  params.append('scope', auth.settings.scope || '');
  params.append('state', oidcId);
  return url;
};

/**
 * A manually written function to try reproducing the steps oidc-client-ts takes
 * to redirect the user to authentication. We need to send the user to almost the
 * same URL as with {@link AuthContextProps.signinRedirect()}, but the path
 * "/connect/authorize" should be replaced with "/user/select".
 *
 * @param config The currently used {@link DynamicConfig}
 * @param auth The currently used auth object from {@link AuthContextProps}
 */
export const changeCompanyRedirect = (config: DynamicConfig, auth: AuthContextProps) => {
  const fakeID = generateOidcId(auth);
  const fakeOIdcObject = generateOidcObj(config, fakeID);

  // Store in local storage
  localStorage.setItem(`${OIDC_STORAGE_PREFIX}${fakeID}`, JSON.stringify(fakeOIdcObject));

  // Generate URL with correct search params and redirect
  const changeCompanyUrl = generateUrlForUserSelect(config, auth, fakeID);
  window.location.href = changeCompanyUrl.toString();
};

/**
 * Remove "oidc.*" entries from local storage that oidc-client-ts can never
 * garbage collect itself, because their "created" timestamp lies in the future.
 *
 * {@link changeCompanyRedirect} used to write "created" in milliseconds while
 * oidc-client-ts expects epoch seconds, so every company switch left an entry
 * behind that its own clearStaleState() would always consider fresh. Those
 * entries are already sitting in users' browsers, so purge them on startup.
 *
 * Deliberately narrow: only a "created" that cannot possibly be legitimate makes
 * an entry eligible. A state for a sign-in that is still in flight is stamped
 * with the current time, so it can never match. Entries with no numeric
 * "created" are left alone, as are unparsable ones - oidc-client-ts removes
 * those itself.
 */
export const purgeUncollectableOidcState = () => {
  // A state created more than a day from now cannot be legitimate.
  const cutoff = epochSeconds() + 60 * 60 * 24;

  const isUncollectable = (key: string): boolean => {
    try {
      const { created } = JSON.parse(localStorage.getItem(key) || '{}');
      return typeof created === 'number' && created > cutoff;
    } catch {
      return false;
    }
  };

  // Storage can be unavailable (private browsing, blocked cookies). Never let
  // housekeeping take down the provider that wraps the whole app.
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(OIDC_STORAGE_PREFIX))
      .filter(isUncollectable)
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore errors
  }
};

/**
 * Will check if the cookie "_UserSelect" is present and compare its value
 * with the logged in user's "issued_at" value. If they're both Dates and
 * "_UserSelect" is newer, it means that our stored user is out of date.
 * @param auth The currently used auth object from {@link AuthContextProps}
 * @returns True if cookie is newer than user's "issued_at"
 */
export const isUserSelectCookieNewer = (auth: AuthContextProps): boolean => {
  const cookieUserSelect = Cookies.get(CookieKeys.USER_SELECT);
  const profile = auth.user?.profile;
  if (cookieUserSelect && isViatelProfile(profile) && profile.issued_at) {
    const cookieDate = new Date(cookieUserSelect);
    const tokenDate = new Date(profile.issued_at);
    return isAfter(cookieDate, tokenDate);
  }
  return false;
};

/**
 * Checks if the current URL contains "openid" and "callback" path segments.
 * @returns {boolean} True if both segments are found, false otherwise.
 */
export const hasRedirectPaths = (): boolean => {
  const pathSegments = window.location.pathname.split('/').filter((segment) => segment !== '');

  const hasOpenId = pathSegments.includes('openid');
  const hasCallback = pathSegments.includes('callback');

  return hasOpenId && hasCallback;
};

/**
 * Removes "openid" and "callback" segments from the current URL.
 * Updates the URL without causing a page reload.
 */
export const clearRedirectPaths = () => {
  const url = new URL(window.location.href);
  let pathSegments = url.pathname.split('/').filter((segment) => segment !== '');

  pathSegments = pathSegments.filter((segment) => segment !== 'openid' && segment !== 'callback');
  url.pathname = pathSegments.join('/');
  history.replaceState(null, '', url.toString());
};

/**
 * Remove any search params in the adress bar related to authentication.
 * Updates the URL without causing a page reload.
 */
export const clearAuthParams = () => {
  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams(currentUrl.search);
  params.delete('code');
  params.delete('scope');
  params.delete('state');
  params.delete('session_state');
  currentUrl.search = params.toString();
  history.replaceState(null, '', currentUrl.toString());
};
